/**
 * Domain extraction utilities for consistent analytics tracking
 * Single source of truth for domain values across all events
 */

import { logger } from '../core/logger';

// Second-level public suffixes where the registrable domain is the last 3 labels
// (eskimoz.co.uk, not co.uk). Not the full public-suffix list — just the ccTLD
// SLDs Rose clients use. Extend as needed.
const MULTI_PART_TLDS = new Set([
    'co.uk',
    'org.uk',
    'gov.uk',
    'ac.uk',
    'com.au',
    'net.au',
    'org.au',
    'co.nz',
    'co.jp',
    'com.br',
    'co.za',
    'com.mx',
    'co.in',
]);

// Global storage for the effective domain (forceDomain if set, otherwise window.location.hostname)
// This is set by the widget initialization and used by all domain utilities
let _effectiveDomain: string | null = null;

/**
 * Set the effective domain to use for all domain utilities
 * This should be called during widget initialization with the forceDomain if provided
 * @param domain - The effective domain to use (forceDomain or actual domain)
 */
export function setEffectiveDomain(domain: string): void {
    _effectiveDomain = domain;
    logger.debug('🌐 Set effective domain:', domain);
}

/**
 * Get the full domain - uses effective domain (forceDomain) if set, otherwise window.location.hostname
 * @returns Full hostname
 * @throws Error if not in browser environment or hostname is empty
 * @example
 * getFullDomain() // "skello.io" (if forceDomain is set)
 * getFullDomain() // "www.pennylane.com" (if no forceDomain)
 * getFullDomain() // "localhost" (if no forceDomain)
 */
export function getFullDomain(): string {
    // Return effective domain if set (forceDomain takes precedence)
    if (_effectiveDomain) {
        return _effectiveDomain;
    }

    // Fallback to window.location.hostname
    if (typeof window === 'undefined') {
        const error = new Error('getFullDomain: Not in browser environment');
        logger.error('⚠️', error);
        throw error;
    }
    const domain = window.location.hostname;
    if (!domain) {
        const error = new Error('getFullDomain: window.location.hostname is empty');
        logger.error('⚠️', error);
        throw error;
    }
    return domain;
}

/**
 * Normalize a domain string to its root domain
 * Handles subdomains, localhost, and IP addresses
 *
 * @param domain - Full domain to normalize (e.g., "www.pennylane.com", "start.pennylane.com")
 * @returns Root domain
 * @example
 * normalizeDomain("www.pennylane.com") // "pennylane.com"
 * normalizeDomain("start.pennylane.com") // "pennylane.com"
 * normalizeDomain("pennylane.com") // "pennylane.com"
 * normalizeDomain("localhost") // "localhost"
 * normalizeDomain("192.168.1.1") // "192.168.1.1"
 * normalizeDomain("www.eskimoz.co.uk") // "eskimoz.co.uk"
 *
 * ponytail: curated multi-part-TLD set instead of the full public-suffix list
 * (~13k entries / 200KB). Covers the ccTLD second-level domains Rose clients
 * actually use; add an entry if a new one appears. Upgrade to `tldts`/PSL only
 * if the long tail ever matters.
 */
export function normalizeDomain(domain: string): string {
    // Handle localhost and IP addresses
    if (domain === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(domain)) {
        return domain;
    }

    const parts = domain.split('.');

    // Multi-part TLDs (e.g. eskimoz.co.uk): keep the last 3 labels so the
    // registrable domain is preserved instead of collapsing to "co.uk".
    if (parts.length >= 3 && MULTI_PART_TLDS.has(parts.slice(-2).join('.'))) {
        return parts.slice(-3).join('.');
    }

    // Default: last 2 parts.
    if (parts.length >= 2) {
        return parts.slice(-2).join('.');
    }

    return domain;
}

/**
 * Extract root/main domain from full hostname
 * Handles subdomains, localhost, and IP addresses
 *
 * @returns Root domain
 * @throws Error if not in browser environment or hostname is empty
 * @example
 * getRootDomain() // "pennylane.com" (from www.pennylane.com)
 * getRootDomain() // "pennylane.com" (from start.pennylane.com)
 * getRootDomain() // "localhost" (from localhost)
 * getRootDomain() // "192.168.1.1" (from 192.168.1.1)
 *
 * Note: This implementation uses a simple "last 2 parts" approach.
 * It does NOT handle multi-level TLDs (.co.uk, .com.au) correctly.
 * For production use with international domains, consider using
 * a public suffix list or probe-based approach.
 */
export function getRootDomain(): string {
    const fullDomain = getFullDomain(); // Will throw if not available
    return normalizeDomain(fullDomain);
}
